import {
	type ForwardedRef,
	forwardRef,
	type ReactElement,
	use,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react"
import { Ripple } from "../Ripple"

import type { SelectProps } from "./SelectProps"
import { getTrue } from "../../utils"
import { useListFunctions } from "../../hooks/useListFunctions"
import selectCss from "./index.module.css"
import cx from "classix"
import { MaterialIcon } from "~/shared/ui"
import { FieldsetContext } from "~/shared/ui/kit/components/Fieldset"

export type { SelectProps }

export type SelectComponent =
	(<C>(props: SelectProps<C>, ref?: ForwardedRef<HTMLUListElement>) => (ReactElement | null))
	& { displayName?: string };

/** Выпадающий список. */
export const Select = forwardRef(function Select<C>({
														name,
														selectStyle,
														onChange,
														valueProp = "value",
														contentProp = "content",
														selectableProp = getTrue,
														disabled = false,
														items = [],
														value,
														defaultValue = "",
														size,
														itemsContainerStyle,
														itemStyle,
														contentStyle,
														placeholder,
														valueIsIndex = false,
														BeforeContentComponent,
														AfterContentComponent,
														ContentWrapper = ({ children }) => <>{children}</>,
														style,
														className,
														onOpen, onClose,
														required = false
													}: SelectProps<C>, ref?: ForwardedRef<HTMLDivElement>) {

	const fs = use(FieldsetContext)
	const isDisabled = disabled || fs.disabled

	const [internalValue, setInternalValue] = useState(defaultValue)

	const isControlled = value !== undefined
	const currentValue = isControlled ? value : internalValue

	const {
		getContent,
		getValue,
		isSelectable
	} = useListFunctions<C>(contentProp, valueProp, selectableProp, valueIsIndex)

	const itemIndex = useMemo(() => items?.findIndex((item, index) => getValue(item, index) === currentValue), [items, getValue, currentValue])
	const item = itemIndex > -1 ? items[itemIndex] : null

	const selectRef = useRef<HTMLDivElement>(null)
	const listRef = useRef<HTMLUListElement>(null)

	const [open, setOpen] = useState(false)

	const handleClick = () => {
		setOpen(true)
		onOpen?.()
	}
	const handleOutsideClick = () => {
		setOpen(false)
		onClose?.()
	}

	useLayoutEffect(() => {

		if (typeof size === "number" && size > 0 && listRef.current && listRef.current.children.length > 0) {

			const options = listRef.current.children

			if (options.length > 0) {
				let h = 0
				const n = Math.min(size, options.length)

				for (let i = 0; i < n; ++i) {
					h += options[i].clientHeight
				}

				const listBcr = listRef.current.getBoundingClientRect()
				const bodyBcr = document.body.getBoundingClientRect()

				const maxHeight = Math.floor(bodyBcr.bottom - listBcr.top)

				listRef.current.style.maxHeight = maxHeight < h
					? `calc(${maxHeight}px - 1rem)`
					: `calc(${h}px + 2px + 1rem)`
			}
		}

	}, [items, size, open])

	return (
		<div ref={ref} style={style} className={cx(selectCss.select, className)}>
			{open && <div onClick={handleOutsideClick} className={selectCss.closeArea} />}
			<div
				ref={selectRef}
				onClick={handleClick}
				style={selectStyle}
				className={cx(selectCss.button, isDisabled && selectCss.disabled)}
			>
				<Ripple color="rgba(0,0,0,0.2)" />
				<span data-placeholder={placeholder ? true : undefined}>
					{item ? getContent(item, itemIndex) : placeholder}
				</span>
				<MaterialIcon size={2} name="arrow_drop_down" style={{ userSelect: "none", width: "2rem" }}
							  className={cx(selectCss.arrow, open && selectCss.arrowOpen)} />
			</div>

			<ul
				style={itemsContainerStyle}
				ref={listRef}
				className={cx(selectCss.options, open && selectCss.open)}
			>
				{
					items.map((item, index) => {

						const curValue = getValue(item, index)

						return (
							<li key={curValue} style={{ display: "flex", alignItems: "center", ...itemStyle }}>
								{BeforeContentComponent && <BeforeContentComponent open={open} item={item} />}
								<ContentWrapper open={open} item={item}>
									<div
										className={selectCss.option}
										style={contentStyle}
										onClick={isSelectable(item, index) ?
											() => {
												setOpen(false)

												if(!isControlled) {
													setInternalValue(curValue)
												}

												onClose?.()
												onChange?.(curValue, item, index)
											} : undefined
										}
									>

										{getContent(item, index)}

									</div>
								</ContentWrapper>
								{AfterContentComponent && <AfterContentComponent open={open} item={item} />}
							</li>
						)
					})
				}
			</ul>
			<input required={required} type="hidden" value={currentValue} name={name} />
		</div>
	)
}) as SelectComponent

Select.displayName = "Select"